import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import React from "react";

import Toggle from "./ui/toggle";
import CheckboxTree from "./checkbox-tree";
import { useSettings } from "../providers/settings-provider";
import { FilterParams } from "./transactions-table";
import { Infinity } from "lucide-react";
import HeaderSearch from "./ui/header-search";

type FiltersProps = {
    status: {
        cashflowMessage: string;
        state: 'good' | 'danger';
        dateInDanger: Date | null;
        daysBeforeNegative?: string;
        // Perf-diagnostic toggle (see /secret-options "Cash Position") is off
        // - the client-side balance/cash-flow calculation is intentionally
        // skipped. Show a neutral dash instead of an infinitely-spinning
        // "Loading...".
        disabled?: boolean;
    };
    unitValue: string;
    setUnitValue: (value: string) => void;
    startDate: Date;
    setStartDate: (value: Date) => void;
    endDate: Date;
    setEndDate: (value: Date) => void;
    isReal: boolean;
    setIsReal: (value: boolean) => void;
    isRecurringProvisions: boolean;
    setIsRecurringProvisions: (value: boolean) => void;
    isOneTimeProvisions: boolean;
    setIsOneTimeProvisions: (value: boolean) => void;
    bank: string[];
    setBank: (value: string[]) => void;
    category: string[];
    setCategory: (value: string[]) => void;
    handleCategoryChange: (value: string[]) => void;
    filterConfig: FilterParams | undefined;
    setFilterConfig: (value: FilterParams | undefined) => void;
    bankOptions: any[];
    categoryOptions: any[];
    onFiltersLoaded: () => void;
    onSelectProvision: () => void;
    appliedSim: { id: string; name: string } | null;
    setAppliedSim: (value: { id: string; name: string } | null) => void;
    favoriteSimulations?: Array<{ id: string; name: string }>;
};

const containerStyle = {
    display: "flex",
    flexDirection: "column",
    width: 150,
    height: 'fit-content',
    justifyContent: "flex-start",
    p: 0.5,
    borderRadius: 1,
    border: '1px solid var(--color-primary)',
    boxSizing: 'border-box',
    gap: 1,
}

const danger = <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 23 20" fill="none">
  <path d="M11.4993 0.340996C12.0947 0.340996 12.644 0.655471 12.9459 1.17121L22.0028 16.6015C22.3089 17.1214 22.3089 17.7629 22.0112 18.2829C21.7134 18.8028 21.1558 19.1257 20.5562 19.1257H2.44238C1.84278 19.1257 1.28511 18.8028 0.987405 18.2829C0.689702 17.7629 0.693895 17.1172 0.995791 16.6015L10.0527 1.17121C10.3546 0.655471 10.9039 0.340996 11.4993 0.340996ZM11.4993 5.70805C10.9416 5.70805 10.493 6.1567 10.493 6.71437V11.4105C10.493 11.9682 10.9416 12.4169 11.4993 12.4169C12.0569 12.4169 12.5056 11.9682 12.5056 11.4105V6.71437C12.5056 6.1567 12.0569 5.70805 11.4993 5.70805ZM12.841 15.1004C12.841 14.7445 12.6997 14.4032 12.448 14.1516C12.1964 13.9 11.8551 13.7586 11.4993 13.7586C11.1434 13.7586 10.8021 13.9 10.5505 14.1516C10.2989 14.4032 10.1575 14.7445 10.1575 15.1004C10.1575 15.4562 10.2989 15.7975 10.5505 16.0492C10.8021 16.3008 11.1434 16.4421 11.4993 16.4421C11.8551 16.4421 12.1964 16.3008 12.448 16.0492C12.6997 15.7975 12.841 15.4562 12.841 15.1004Z" fill="#A80000"/>
</svg>

const good = <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 23 23" fill="none">
  <path d="M11.5 23C14.55 23 17.4751 21.7884 19.6317 19.6317C21.7884 17.4751 23 14.55 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0C8.45001 0 5.52494 1.2116 3.36827 3.36827C1.2116 5.52494 0 8.45001 0 11.5C0 14.55 1.2116 17.4751 3.36827 19.6317C5.52494 21.7884 8.45001 23 11.5 23ZM16.5762 9.38867L10.8262 15.1387C10.4039 15.5609 9.72109 15.5609 9.30332 15.1387L6.42832 12.2637C6.00605 11.8414 6.00605 11.1586 6.42832 10.7408C6.85059 10.323 7.5334 10.3186 7.95117 10.7408L10.0625 12.8521L15.0488 7.86133C15.4711 7.43906 16.1539 7.43906 16.5717 7.86133C16.9895 8.28359 16.9939 8.96641 16.5717 9.38418L16.5762 9.38867Z" fill="#0D6832"/>
</svg>;

const info = <svg width="40" height="40" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="#756a00"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#756a00" d="M512 64a448 448 0 1 1 0 896.064A448 448 0 0 1 512 64zm67.2 275.072c33.28 0 60.288-23.104 60.288-57.344s-27.072-57.344-60.288-57.344c-33.28 0-60.16 23.104-60.16 57.344s26.88 57.344 60.16 57.344zM590.912 699.2c0-6.848 2.368-24.64 1.024-34.752l-52.608 60.544c-10.88 11.456-24.512 19.392-30.912 17.28a12.992 12.992 0 0 1-8.256-14.72l87.68-276.992c7.168-35.136-12.544-67.2-54.336-71.296-44.096 0-108.992 44.736-148.48 101.504 0 6.784-1.28 23.68.064 33.792l52.544-60.608c10.88-11.328 23.552-19.328 29.952-17.152a12.8 12.8 0 0 1 7.808 16.128L388.48 728.576c-10.048 32.256 8.96 63.872 55.04 71.04 67.84 0 107.904-43.648 147.456-100.416z"></path></g></svg>;

const Filters: React.FC<FiltersProps> = ({ 
    status,
    isReal, 
    setIsReal, 
    isRecurringProvisions,
    setIsRecurringProvisions,
    isOneTimeProvisions,
    setIsOneTimeProvisions,
    bank,
    setBank,
    category,
    handleCategoryChange,
    bankOptions,
    categoryOptions,
    onSelectProvision,
    appliedSim,
    setAppliedSim,
    favoriteSimulations = [],
    filterConfig,
    setFilterConfig
}) => {
    const { formatDate } = useSettings();

    return (
        <Box display="flex" alignItems="center" width="100%" height={64} pl={1} gap={0.75}>
            <Box sx={{ ...containerStyle, width: 'fit-content', flexDirection: 'row', gap: 1, py: 0.75, alignItems: 'center' }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                        <Typography sx={{ textWrap: 'nowrap', fontSize: 11, color: 'text.primary' }}>Real Transactions</Typography>
                        <Toggle checked={isReal} onChange={setIsReal} size="smaller"/>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                        <Typography sx={{ textWrap: 'nowrap', fontSize: 11, color: 'text.primary' }}>Recurring Provisions</Typography>
                        <Toggle checked={isRecurringProvisions} onChange={setIsRecurringProvisions} size="smaller"/>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                        <Typography sx={{ textWrap: 'nowrap', fontSize: 11, color: 'text.primary' }}>One-time Provisions</Typography>
                        <Toggle checked={isOneTimeProvisions} onChange={setIsOneTimeProvisions} size="smaller"/>
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={0.25}>
                    <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap' }} variant="outlined" onClick={onSelectProvision}>{appliedSim?.name || 'Select Simulation'}</Button>
                    <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap' }} variant="outlined" onClick={() => setAppliedSim(null)} disabled={!appliedSim}>Clear Simulation</Button>
                </Box>
            </Box>
            <Box sx={{ ...containerStyle, width: 150, gap: 0.5, border: 'none' }}>
                <CheckboxTree
                    treeOptions={bankOptions}
                    value={bank}
                    onChange={(newBanks) => {
                        const processedBanks = newBanks.filter(id => !['select-all-banks'].includes(id));
                        setBank(processedBanks);
                    }}
                    placeholder="Select Banks"
                    countLabel="banks"
                />
                <CheckboxTree
                    treeOptions={[
                        { 
                            id: 'select-all-categories', 
                            label: 'Select All', 
                            isSpecial: true, 
                            specialType: 'selectAll' as const 
                        },
                        { 
                            id: 'uncategorized', 
                            label: 'Uncategorized', 
                            specialType: 'uncategorized' as const 
                        },
                        ...categoryOptions.filter((cat: any) => cat.label !== 'Select All')
                    ]}
                    value={category}
                    onChange={handleCategoryChange}
                    placeholder="Select Categories"
                    countLabel="categories"
                />
            </Box>
            <Box sx={{ ...containerStyle, width: 'fit-content', px: 1, border: 'none' }}>
                <Tooltip title={status.disabled ? "Cash position calculation is turned off" : status.daysBeforeNegative === '∞' ? "You never run out of money" : "Days before you run out of money"} placement="bottom" arrow sx={{ zIndex: theme => theme.zIndex.tooltip }}>
                    <Typography sx={{ textWrap: 'nowrap', fontSize: 30, color: 'text.primary', fontWeight: 'bold' }}>
                        {status.disabled ? '-' : status.daysBeforeNegative === '∞' ? <Infinity size={40} /> : status.daysBeforeNegative}
                    </Typography>
                </Tooltip>
            </Box>
            <Box
                sx={{
                    ...containerStyle,
                    width: 200,
                    border: 'none',
                    background: (status.disabled || !status.cashflowMessage) ? '#E8E8E8' : status.cashflowMessage === 'No transactions available to determine cash flow status.' ? '#ffcc0052' : status.cashflowMessage.includes('never') ? 'rgba(var(--color-cashflow-positive-bg-rgb), 0.20)' : 'rgba(var(--color-cashflow-negative-bg-rgb), 0.1882)',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    mr: 1,
                    height: 62,
                    borderRadius: 1,
                }}
            >
                {status.disabled ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'center' }}>
                        <Typography sx={{fontSize: 10.5, color: 'var(--color-text-muted)' }}>Cash position off</Typography>
                    </Box>
                ) : !status.cashflowMessage ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'center' }}>
                        <CircularProgress size={16} />
                        <Typography sx={{fontSize: 10.5, color: 'var(--color-text-muted)' }}>Loading...</Typography>
                    </Box>
                ) : (
                    <>
                        {status.cashflowMessage === 'No transactions available to determine cash flow status.' ? info : status.cashflowMessage.includes('never') ? good : danger}
                        <Typography 
                            sx={{fontSize: 12, color: status.cashflowMessage === 'No transactions available to determine cash flow status.' ? '#4b4400' : status.cashflowMessage.includes('never') ? 'var(--color-cashflow-positive-text)' : 'var(--color-cashflow-negative-text)' }}>
                                {
                                    status.cashflowMessage.includes('|') 
                                        ? <>
                                            {status.cashflowMessage.split('|')[0]}
                                            <b>{formatDate(status.dateInDanger || new Date())}</b>
                                        </>
                                        : status.cashflowMessage
                                }
                        </Typography>
                    </>
                )}
            </Box>
            <HeaderSearch
                value={filterConfig?.searchKeyword}
                onSearch={(v) => setFilterConfig({ ...(filterConfig || {}), searchKeyword: v || undefined } as FilterParams)}
                placeholder="Search transactions..."
                width={200}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 0.5, mr: 1 }}>
                {(favoriteSimulations && favoriteSimulations.length > 0 ? favoriteSimulations : []).map(sim => (
                    <Button key={sim.id} size="small" sx={{ fontSize: 10, height: 25, width: 120, textWrap: 'nowrap', borderColor: appliedSim?.id !== sim.id ? '#bdbdbd' : 'default', color: appliedSim?.id !== sim.id ? '#737373' : '#fff', fontWeight: appliedSim?.id !== sim.id ? 'normal' : 'bold' }} variant={appliedSim?.id !== sim.id ? "outlined" : "contained"} onClick={() => setAppliedSim({ id: sim.id, name: sim.name })}>{sim.name}</Button>
                    ))
                }
            </Box>
        </Box>
    );
};

export { Filters };
export type { FiltersProps };
